import React, { useState, useEffect } from 'react';
import { UserPlus, Shield, Mail, Key, Trash2, Edit, X, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const UsersManagement = ({ apiKey }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'editor'
  });

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/users`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${apiUrl}/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchUsers();
        setShowAddForm(false);
        resetForm();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur de connexion');
    }
  };

  const handleUpdate = async (userId) => {
    try {
      const response = await fetch(`${apiUrl}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchUsers();
        setEditingId(null);
        resetForm();
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Impossible de supprimer cet utilisateur');
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', role: 'editor' });
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold flex items-center gap-1"><Shield className="h-3 w-3" />Admin</span>,
      editor: <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold flex items-center gap-1"><Edit className="h-3 w-3" />Éditeur</span>,
      viewer: <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">Lecteur</span>
    };
    return badges[role] || role;
  };

  const getStatusBadge = (status) => {
    return status === 'active' 
      ? <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">Actif</span>
      : <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">Inactif</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Utilisateurs</h2>
          <p className="text-gray-600 mt-1">{users.length} utilisateur(s) au total</p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Nouvel Utilisateur
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-4">Ajouter un nouvel utilisateur</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="viewer">Lecteur</option>
                  <option value="editor">Éditeur</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-green-500 hover:bg-green-600 text-white">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Créer l'utilisateur
                </Button>
                <Button
                  type="button"
                  onClick={() => { setShowAddForm(false); resetForm(); }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Roles Info */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Rôles et Permissions
          </h3>
          <div className="space-y-2 text-sm text-blue-800">
            <div className="flex items-start gap-2">
              <span className="font-bold">Admin:</span>
              <span>Accès complet - Gestion du contenu, paramètres, utilisateurs, statistiques</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold">Éditeur:</span>
              <span>Gestion du contenu - Blog, portfolio, avis, contacts (pas de paramètres)</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold">Lecteur:</span>
              <span>Lecture seule - Consultation des statistiques et données uniquement</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="space-y-4">
        {users.map((user) => (
          <Card key={user.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              {editingId === user.id ? (
                <form onSubmit={(e) => { e.preventDefault(); handleUpdate(user.id); }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nom</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Rôle</label>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="viewer">Lecteur</option>
                        <option value="editor">Éditeur</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-green-500 hover:bg-green-600 text-white">
                      <Save className="h-4 w-4 mr-2" />
                      Enregistrer
                    </Button>
                    <Button
                      type="button"
                      onClick={() => { setEditingId(null); resetForm(); }}
                      className="bg-gray-500 hover:bg-gray-600 text-white"
                    >
                      Annuler
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <h3 className="text-xl font-bold text-gray-900">{user.name}</h3>
                      {getRoleBadge(user.role)}
                      {getStatusBadge(user.status)}
                    </div>

                    <div className="flex items-center gap-6 text-gray-600 mb-2">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>{user.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        <span>Créé le {formatDate(user.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => startEdit(user)}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(user.id)}
                      disabled={user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1}
                      className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Box */}
      <Card className="mt-6 bg-green-50 border-green-200">
        <CardContent className="p-6">
          <h3 className="font-bold text-green-900 mb-2">✅ Gestion Utilisateurs Active</h3>
          <p className="text-sm text-green-800">
            Système complet connecté à MongoDB avec API CRUD. Les utilisateurs peuvent être créés, modifiés et supprimés. 
            Protection : le dernier admin ne peut pas être supprimé.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersManagement;
